/**
 * Venda parcelada (chamado 2026-0067) — as regras puras da tela.
 *
 * Uma venda em n parcelas vira n recebimentos no backend (o rateio e as datas são decididos
 * lá, em ParcelamentoDeRecebimento.php). Aqui só o que a tela precisa: quantas parcelas a forma
 * aceita, o texto de cada opção ("3x de R$ 33,33") e o agrupamento das n linhas de volta numa
 * venda para as listas.
 */

/** O limite que era fixo no form do compromisso — o mesmo PARCELAS_MAX_PADRAO do backend. */
export const PARCELAS_MAX_PADRAO = 12

export interface FormaComParcelamento {
  forma_pagamento_codigo?: string | null
  forma_pagamento_aceita_parcelado?: number | string | null
  forma_pagamento_parcelas_max?: number | string | null
  forma_pagamento_parcelas_max_efetivo?: number | null
}

/**
 * Quantas parcelas a forma aceita (1 = não parcela). Usa o valor que o backend já calculou;
 * sem ele (lista de formas vinda de um cache anterior ao campo), deriva pela mesma regra.
 */
export function parcelasMaxDaForma(forma?: FormaComParcelamento | null): number {
  if (!forma) return 1
  if (typeof forma.forma_pagamento_parcelas_max_efetivo === 'number') {
    return Math.max(1, forma.forma_pagamento_parcelas_max_efetivo)
  }
  if (forma.forma_pagamento_codigo === 'CASH') return 1
  if (Number(forma.forma_pagamento_aceita_parcelado ?? 0) !== 1) return 1
  const max = forma.forma_pagamento_parcelas_max
  return max === null || max === undefined || max === '' ? PARCELAS_MAX_PADRAO : Math.max(1, Number(max))
}

/** O mesmo rateio do backend: partes iguais, a sobra na ÚLTIMA — a soma bate no centavo. */
export function rateiaCentavos(total: number, n: number): number[] {
  if (!Number.isInteger(n) || n < 1) throw new RangeError(`parcelas deve ser >= 1, recebido ${n}`)
  const base = Math.trunc(total / n)
  const partes = Array.from({ length: n }, () => base)
  partes[n - 1] += total - base * n
  return partes
}

export interface OpcaoDeParcelas {
  ID: number
  Name: string
}

/**
 * As opções 1..max, cada uma com o valor da parcela. Quando o rateio não é exato, a última
 * parcela é maior e o texto diz ("3x de R$ 33,33 (última R$ 33,34)").
 *
 * @param formata recebe o valor em unidades da moeda (reais), devolve o texto
 * @param rotulos os textos já traduzidos (a tela passa t(...))
 */
export function opcoesDeParcelas(
  totalCentavos: number,
  max: number,
  formata: (valor: number) => string,
  rotulos: { aVista: (valor: string) => string; parcelado: (n: number, valor: string) => string; ultima: (valor: string) => string },
): OpcaoDeParcelas[] {
  const opcoes: OpcaoDeParcelas[] = []
  for (let n = 1; n <= Math.max(1, max); n++) {
    if (n === 1) {
      opcoes.push({ ID: 1, Name: rotulos.aVista(formata(totalCentavos / 100)) })
      continue
    }
    const partes = rateiaCentavos(totalCentavos, n)
    let texto = rotulos.parcelado(n, formata(partes[0] / 100))
    if (partes[n - 1] !== partes[0]) texto += ` ${rotulos.ultima(formata(partes[n - 1] / 100))}`
    opcoes.push({ ID: n, Name: texto })
  }
  return opcoes
}

/** O que a lista precisa de cada linha para agrupar. */
export interface LinhaDeRecebimento {
  recebimento_id?: number | string
  recebimento_valor?: number | null
  recebimento_data_disponivel?: string | null
  recebimento_numero_parcelas?: number | null
  recebimento_parcela_numero?: number | null
  recebimento_parcela_de?: number | null
}

export interface GrupoDeParcelas<T extends LinhaDeRecebimento> {
  /** o id da parcela 1 — é o que se abre para editar/apagar a venda */
  chave: number
  linhas: T[]
  numeroParcelas: number
  valorTotal: number
  /** parcelas cuja data disponível já chegou (inclusive hoje) */
  disponiveis: number
}

/** A chave do grupo de uma linha: o id da parcela 1 (ela mesma quando à vista ou parcela 1). */
export function chaveDoGrupo(linha: LinhaDeRecebimento): number {
  return Number(linha.recebimento_parcela_de ?? linha.recebimento_id ?? 0)
}

/**
 * As linhas de volta em vendas, na ordem em que a primeira linha de cada venda aparece; dentro
 * de cada venda, na ordem das parcelas.
 *
 * @param hoje YYYY-MM-DD local — comparação em texto, sem fuso (ver RecebimentoForm)
 */
export function agrupaParcelas<T extends LinhaDeRecebimento>(linhas: T[], hoje: string): GrupoDeParcelas<T>[] {
  const grupos = new Map<number, T[]>()
  for (const linha of linhas) {
    const chave = chaveDoGrupo(linha)
    const grupo = grupos.get(chave)
    if (grupo) grupo.push(linha)
    else grupos.set(chave, [linha])
  }

  return [...grupos.entries()].map(([chave, doGrupo]) => {
    const ordenadas = [...doGrupo].sort((a, b) => Number(a.recebimento_parcela_numero ?? 1) - Number(b.recebimento_parcela_numero ?? 1))
    return {
      chave,
      linhas: ordenadas,
      numeroParcelas: Math.max(ordenadas.length, Number(ordenadas[0].recebimento_numero_parcelas ?? 1)),
      valorTotal: ordenadas.reduce((soma, l) => soma + Number(l.recebimento_valor ?? 0), 0),
      disponiveis: ordenadas.filter((l) => { const d = dataIso(l.recebimento_data_disponivel); return d !== '' && d <= hoje }).length,
    }
  })
}

/**
 * Uma data de recebimento em YYYY-MM-DD, venha como vier: as listas do backend passam as colunas
 * DATE pelo Record (record_helper::to_object), que as formata em DD/MM/YYYY; o carregar devolve
 * YYYY-MM-DD. Comparar ou editar o texto cru misturava os dois. Vazio/inválido → ''.
 */
export function dataIso(valor: unknown): string {
  const texto = String(valor ?? '').trim()
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(texto)
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`
  const br = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(texto)
  if (br) return `${br[3]}-${br[2]}-${br[1]}`
  return ''
}

/** Hoje em YYYY-MM-DD no fuso LOCAL (toISOString daria UTC e erraria à noite no Brasil). */
export function hojeLocal(agora: Date = new Date()): string {
  return `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}-${String(agora.getDate()).padStart(2, '0')}`
}

/**
 * As recusas do parcelamento chegam do backend como código (ParcelamentoDeRecebimento::recusa e
 * a edição de parcela em recebimentos.php); a pessoa lê a frase. null = não é uma recusa daqui.
 */
export function mensagemDaRecusaDeParcelamento(erro: unknown, t: (chave: string) => string): string | null {
  const codigo = erro instanceof Error ? erro.message : typeof erro === 'string' ? erro : ''
  const mensagens: Record<string, string> = {
    parcelamento_nao_aceito: 'Esta forma de pagamento não aceita parcelamento.',
    parcelas_acima_do_maximo: 'O número de parcelas passa do máximo desta forma de pagamento.',
    parcelamento_sem_forma_de_pagamento: 'Escolha a forma de pagamento antes de parcelar.',
    parcelamento_so_na_criacao: 'O número de parcelas só é escolhido ao registrar a venda.',
    parcela_nao_edita_valor: 'O valor de uma parcela não muda — para mudar a venda, exclua-a e registre de novo.',
    taxa_maior_que_a_parcela: 'A taxa de uma parcela não pode passar do valor dela.',
    parcela_fora_da_venda: 'Uma das parcelas não pertence a esta venda. Recarregue e tente de novo.',
    recebimento_nao_e_parcelado: 'Este recebimento não é uma venda parcelada.',
  }
  return codigo in mensagens ? t(mensagens[codigo]) : null
}

/** Uma parcela como a tela de venda parcelada edita. Valores em centavos. */
export interface ParcelaEditavel {
  recebimento_id: number
  recebimento_parcela_numero: number
  recebimento_valor: number
  recebimento_valor_taxas: number
  /** YYYY-MM-DD */
  recebimento_data_disponivel: string
}

/** Totais da venda: o que o cliente pagou, as taxas e o que sobra — a soma das parcelas. */
export function totaisDaVenda(parcelas: Pick<ParcelaEditavel, 'recebimento_valor' | 'recebimento_valor_taxas'>[]): { valor: number; taxas: number; liquido: number } {
  const valor = parcelas.reduce((s, p) => s + Number(p.recebimento_valor || 0), 0)
  const taxas = parcelas.reduce((s, p) => s + Number(p.recebimento_valor_taxas || 0), 0)
  return { valor, taxas, liquido: valor - taxas }
}

export interface MudancaDeParcela {
  recebimento_id: number
  recebimento_valor_taxas?: number
}

/**
 * Só as taxas que mudaram, para o salvar_parcelas — uma parcela intocada não vai no payload. A
 * data disponível não se edita (vem do prazo da forma ou do provedor).
 */
export function mudancasDasParcelas(originais: ParcelaEditavel[], editadas: ParcelaEditavel[]): MudancaDeParcela[] {
  const porId = new Map(originais.map((p) => [p.recebimento_id, p]))
  const mudancas: MudancaDeParcela[] = []
  for (const e of editadas) {
    const o = porId.get(e.recebimento_id)
    if (!o) continue
    const m: MudancaDeParcela = { recebimento_id: e.recebimento_id }
    if (Number(e.recebimento_valor_taxas) !== Number(o.recebimento_valor_taxas)) m.recebimento_valor_taxas = Number(e.recebimento_valor_taxas)
    if (Object.keys(m).length > 1) mudancas.push(m)
  }
  return mudancas
}
